import moment from 'moment';

import { FORTA_BLUE } from '../../../utils/colors';

import type { ChildBackgroundConfig } from './types';

export const CHILD_BACKGROUND_TYPE = {
  COMMUNICATION_STYLE: 'COMMUNICATION_STYLE',
  GOALS_FOR_THERAPY: 'GOALS_FOR_THERAPY',
  STRENGTH: 'STRENGTH',
  WEAKNESS: 'WEAKNESS',
  INTEREST: 'INTEREST',
  DISLIKES: 'DISLIKES',
  BEHAVIOR_ISSUE: 'BEHAVIOR_ISSUE',
} as const;

export const DAY_OF_WEEK = {
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
} as const;

const COMMUNICATION_STYLE_OPTIONS = [
  {
    value: 'Verbal communication (single words, sentences, etc.)',
    label: 'Verbal communication (single words, sentences, etc.)',
  },
  { value: 'Speech device (AAC, tablet, etc.)', label: 'Speech device (AAC, tablet, etc.)' },
  { value: 'Sign language', label: 'Sign language' },
  { value: 'Gestures (pointing, pulling, etc.)', label: 'Gestures (pointing, pulling, etc.)' },
  {
    value: 'Picture-based communication (PECS, picture boards, etc.)',
    label: 'Picture-based communication (PECS, picture boards, etc.)',
  },
  { value: 'No communication', label: 'No communication' },
] as const;

const GOALS_FOR_THERAPY_OPTIONS = [
  { value: 'Improve communication skills', label: 'Improve communication skills' },
  { value: 'Reduce tantrums or meltdowns', label: 'Reduce tantrums or meltdowns' },
  { value: 'Help with following directions', label: 'Help with following directions' },
  { value: 'Support potty training', label: 'Support potty training' },
  {
    value: 'Improve social skills (making friends, sharing, taking turns)',
    label: 'Improve social skills (making friends, sharing, taking turns)',
  },
  { value: 'Decrease concerning behaviors', label: 'Decrease concerning behaviors' },
  { value: 'Improve attention and focus', label: 'Improve attention and focus' },
  {
    value: 'Help with transitions between activities',
    label: 'Help with transitions between activities',
  },
  {
    value: 'Increase independence with daily living skills (dressing, brushing teeth, feeding)',
    label: 'Increase independence with daily living skills (dressing, brushing teeth, feeding)',
  },
  { value: 'Support school readiness skills', label: 'Support school readiness skills' },
  { value: 'Improve play skills', label: 'Improve play skills' },
  { value: 'Reduce repetitive behaviors', label: 'Reduce repetitive behaviors' },
  {
    value: 'Improve safety skills (staying nearby, crossing streets safely)',
    label: 'Improve safety skills (staying nearby, crossing streets safely)',
  },
  { value: 'Support emotional regulation', label: 'Support emotional regulation' },
  { value: 'Help with sleep routines', label: 'Help with sleep routines' },
  {
    value: 'Support feeding or picky eating concerns',
    label: 'Support feeding or picky eating concerns',
  },
  { value: 'General developmental support', label: 'General developmental support' },
] as const;

const BEHAVIOR_ISSUE_OPTIONS = [
  {
    value: 'Aggression (e.g., hitting, kicking, biting)',
    label: 'Aggression (e.g., hitting, kicking, biting)',
  },
  {
    value: 'Verbal aggression (e.g., threats, name-calling)',
    label: 'Verbal aggression (e.g., threats, name-calling)',
  },
  {
    value: 'Elopement (e.g., running from caregiver)',
    label: 'Elopement (e.g., running from caregiver)',
  },
  {
    value: 'Property destruction (e.g., breaking toys, household items)',
    label: 'Property destruction (e.g., breaking toys, household items)',
  },
  {
    value: 'Task refusal (e.g., refusing instructions or tasks)',
    label: 'Task refusal (e.g., refusing instructions or tasks)',
  },
  {
    value: 'Self-injury (e.g., hitting or pinching itself)',
    label: 'Self-injury (e.g., hitting or pinching itself)',
  },
  {
    value: 'Pica (Eating non-food items such as plastic, chalk, fabric, etc.)',
    label: 'Pica (Eating non-food items such as plastic, chalk, fabric, etc.)',
  },
  {
    value: 'Disrobing (Removal of clothing in inappropriate settings, etc.)',
    label: 'Disrobing (Removal of clothing in inappropriate settings, etc.)',
  },
  { value: 'None', label: 'None' },
] as const;

// One selection list per child-background type. The added value is stored as background_description.
export const CHILD_BACKGROUND_CONFIGS: ReadonlyArray<ChildBackgroundConfig> = [
  {
    type: CHILD_BACKGROUND_TYPE.COMMUNICATION_STYLE,
    label: 'Child communication style',
    addButtonLabel: 'Add comm. style',
    placeholder: 'Select a communication style',
    options: COMMUNICATION_STYLE_OPTIONS,
  },
  {
    type: CHILD_BACKGROUND_TYPE.GOALS_FOR_THERAPY,
    label: 'Goals for therapy',
    addButtonLabel: 'Add goal',
    placeholder: 'Select a goal',
    options: GOALS_FOR_THERAPY_OPTIONS,
  },
  {
    type: CHILD_BACKGROUND_TYPE.STRENGTH,
    label: "Child's strengths (at least 2)",
    addButtonLabel: 'Enter strength',
    placeholder: 'Enter a strength',
    freeText: true,
    minItems: 2,
  },
  {
    type: CHILD_BACKGROUND_TYPE.WEAKNESS,
    label: "Child's weaknesses (at least 2)",
    addButtonLabel: 'Enter weakness',
    placeholder: 'Enter a weakness',
    freeText: true,
    minItems: 2,
  },
  {
    type: CHILD_BACKGROUND_TYPE.INTEREST,
    label: "Child's interests (at least 2)",
    addButtonLabel: 'Enter interest',
    placeholder: 'Enter an interest',
    freeText: true,
    minItems: 2,
  },
  {
    type: CHILD_BACKGROUND_TYPE.DISLIKES,
    label: "Child's dislikes (at least 2)",
    addButtonLabel: 'Enter dislike',
    placeholder: 'Enter a dislike',
    freeText: true,
    minItems: 2,
  },
  {
    type: CHILD_BACKGROUND_TYPE.BEHAVIOR_ISSUE,
    label: "Child's behavior issues",
    addButtonLabel: 'Add behavior',
    placeholder: 'Select behavior issues',
    options: BEHAVIOR_ISSUE_OPTIONS,
  },
];

export const SCHEDULE_DAY_OPTIONS = Object.values(DAY_OF_WEEK).map((day) => ({
  value: day,
  label: `${day.charAt(0)}${day.slice(1).toLowerCase()}`,
}));

const SCHEDULE_DAY_START_HOUR = 5;
export const SCHEDULE_DAY_END_HOUR = 24;

export const SCHEDULE_START_TIME_OPTIONS = Array.from(
  { length: SCHEDULE_DAY_END_HOUR - SCHEDULE_DAY_START_HOUR },
  (_, index) => ({
    value: SCHEDULE_DAY_START_HOUR + index,
    label: moment({ hour: (SCHEDULE_DAY_START_HOUR + index) % 24 }).format('h:mm A'),
  }),
);

export const SCHEDULE_END_TIME_OPTIONS = Array.from(
  { length: SCHEDULE_DAY_END_HOUR - SCHEDULE_DAY_START_HOUR },
  (_, index) => ({
    value: SCHEDULE_DAY_START_HOUR + 1 + index,
    label: moment({ hour: (SCHEDULE_DAY_START_HOUR + 1 + index) % 24 }).format('h:mm A'),
  }),
);

export const SCHEDULE_ACTIVITY_TYPE = {
  FORTA_THERAPY: 'FORTA_THERAPY',
  SCHOOL: 'SCHOOL',
  OTHER_THERAPY: 'OTHER_THERAPY',
  OTHER_ACTIVITY: 'OTHER_ACTIVITY',
} as const;

export const SCHEDULE_ACTIVITY_OPTIONS = [
  { value: SCHEDULE_ACTIVITY_TYPE.FORTA_THERAPY, label: 'Available for Forta therapy' },
  { value: SCHEDULE_ACTIVITY_TYPE.SCHOOL, label: 'School' },
  { value: SCHEDULE_ACTIVITY_TYPE.OTHER_THERAPY, label: 'Other therapies' },
  { value: SCHEDULE_ACTIVITY_TYPE.OTHER_ACTIVITY, label: 'Other activities' },
] as const;

export const SCHEDULE_ACTIVITY_COLORS = {
  [SCHEDULE_ACTIVITY_TYPE.FORTA_THERAPY]: FORTA_BLUE.primary,
  [SCHEDULE_ACTIVITY_TYPE.SCHOOL]: '#34A853',
  [SCHEDULE_ACTIVITY_TYPE.OTHER_THERAPY]: '#F07A03',
  [SCHEDULE_ACTIVITY_TYPE.OTHER_ACTIVITY]: '#0B98EF',
};
